/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ICacheKey } from '@msdyn365-commerce/cache-internal';
import {
    CoreContext,
    getPerformanceUtils,
    IAddModuleSlot,
    IAny,
    IDictionary,
    IGeneric,
    IModule,
    IModuleContract,
    isAuthoringEditEnabled,
    msdyn365Commerce,
    SDK_FRAGMENT_NAME,
    WithContext
} from '@msdyn365-commerce/core-internal';
import LazyLoad from 'react-lazy-load';
import {
    asSystemMetadata,
    EXCEPTION_MISSINGMODULECONFIG,
    EXCEPTION_MODULERENDERFAIL,
    EXCEPTION_NOMODULEBINDER,
    EXCEPTION_UNREGISTEREDMODULEID,
    InternalTelemetry,
    ITelemetry,
    LogLevel
} from '@msdyn365-commerce/telemetry-internal';
import { get as _get } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { AsyncClientRenderComponent } from './module-client-render-component';
import {
    LAYOUT_MOUNT_POINT,
    MODULE_CONFIG_ERRORS_KEY,
    MODULE_LAYOUT_FIELD,
    SERVER_RENDER_ERROR_CACHE_KEY,
    MODULE_CONFIG_KEY
} from '../consts';
import { PageContext } from '../store/page-context';
import { AddControlMode, AddModuleControl } from './add-module-control';
import { AddModuleWrapper } from './add-module-wrapper';
import { ErrorModule, renderErrorOrPlaceHolderModules } from './error-module';
import { renderView } from './render-view';
import { isLazyRenderModuleEnabled } from '../utils/helpers';

type ErrorInfo = {
    componentStack: string;
};

export type TSafeRenderModuleState = {
    error?: Error;
    info?: ErrorInfo;
    isRegistered?: boolean;
    isConfigured?: boolean;
    isLazyLoaded?: boolean;
    mounted?: boolean;
};

/**
 * Known types on the props
 */
export type TSafeRenderModuleProps = IGeneric<IAny> & {
    telemetry: ITelemetry;
    internalTelemetry: InternalTelemetry;
};

/**
 * Component to render module
 */
@observer
export class SafeRenderModule extends React.Component<TSafeRenderModuleProps, TSafeRenderModuleState> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private componentRef: React.RefObject<React.Component<{}, any>>;
    private shouldDisplayErrors: boolean = false;
    private isRenderAttrributeCalled: boolean = false;
    private disableSSRErrorChecks?: boolean = false;
    private renderCallsCounter = 0;
    private mobxShouldComponentUpdate;

    public static getDerivedStateFromError(error: Error): TSafeRenderModuleState {
        return { error: error };
    }

    constructor(props: TSafeRenderModuleProps) {
        super(props);

        this.state = {
            error: undefined,
            info: undefined,
            isConfigured: true,
            isRegistered: true,
            mounted: false
        };
        this.componentRef = React.createRef();
        this.shouldDisplayErrors = props && props.context ? props.context.request.params.isDebug : true;

        // If App Setting to disable server-side rendering error checks is true then instead of rendering each module
        // separately and potentially containing each one in an error-boundary in case of an error,
        // safe-render-module will instead render all modules together in one pass for improved performance.
        this.disableSSRErrorChecks = props.context && props.app && props.app.platform && props.app.platform.disableServerSideErrorChecking;

        // In the event that this is being rendered in editor or debug mode we will override this setting to render each individual module separately
        // to catch module errors at the cost of performance
        this.disableSSRErrorChecks =
            this.disableSSRErrorChecks && !props.context.request.params.isDebug && !props.context.request.params.isEditor;

        this.props.internalTelemetry.log(
            LogLevel.Debug,
            `Safe Render module constructor type-'{typeName}' with id-'{id} and is ${
                this.disableSSRErrorChecks ? 'not ' : ''
            }running in error check mode.'`,
            {
                values: [asSystemMetadata(this.props.typeName), asSystemMetadata(this.props.id)]
            }
        );

        this.mobxShouldComponentUpdate = this.shouldComponentUpdate;
        this.shouldComponentUpdate = this.customShouldComponentUpdate;
    }

    public componentDidMount(): void {
        if (!this.state.mounted) {
            this.setState({ mounted: true });
        }

        if (this.isRenderAttrributeCalled) {
            return;
        }

        this._setErrorStates();
        this._setDataAttributes();

        this._initializePerformanceComponent();
    }

    public customShouldComponentUpdate(nextProps: TSafeRenderModuleProps, nextState: TSafeRenderModuleState): boolean {
        if (this._isEditorial()) {
            if (nextState.error) {
                this.props.store.requestCache.put(
                    { typeName: MODULE_CONFIG_ERRORS_KEY, key: this.props.id },
                    { item: [{ property: this.props.id, message: nextState.error.message }] }
                );
            }
        }

        return this.mobxShouldComponentUpdate(nextProps, nextState);
    }

    public componentDidUpdate(): void {
        if (this.isRenderAttrributeCalled) {
            return;
        }

        this._setErrorStates();
        this._setDataAttributes();
    }

    public componentDidCatch(error: Error, info: ErrorInfo): void {
        this.setState({ error, info });
    }

    public render(): JSX.Element | null {
        const performanceUtils = getPerformanceUtils(msdyn365Commerce, this.props.context.actionContext);

        const renderId = performanceUtils?.performanceUtilsPackage.Random.Guid.generateGuid();

        performanceUtils?.performanceUtilsPackage.runPreRenderPerformanceHook({
            moduleId: this.props.id,
            moduleTypeName: this.props.typeName,

            renderId: renderId,
            renderCallIndex: ++this.renderCallsCounter,

            internalCache: performanceUtils.cache
        });

        const node = this._renderModule();

        performanceUtils?.performanceUtilsPackage.runPostRenderPerformanceHook({
            moduleId: this.props.id,
            moduleTypeName: this.props.typeName,

            renderId: renderId,

            internalCache: performanceUtils.cache
        });

        return node;
    }

    private _initializePerformanceComponent(): void {
        const performanceUtils = getPerformanceUtils(msdyn365Commerce, this.props.context.actionContext);
        performanceUtils?.performanceUtilsPackage.initializePerformance(
            () => performanceUtils.cache,
            () => this.props.context.actionContext
        );
    }

    // eslint-disable-next-line complexity
    private _renderModule(): JSX.Element | null {
        const config = this.props.config;
        const { internalTelemetry } = this.props;
        internalTelemetry.log(LogLevel.Debug, `Safe Render module render type-'{typeName}' with id-'{id}'`, {
            values: [asSystemMetadata(this.props.typeName), asSystemMetadata(this.props.id)]
        });
        const isMounted = this.state.mounted;
        const mappedProps = mapStateToProps(this.props.store, this.props, isMounted);
        const { id, typeName } = mappedProps && mappedProps.id ? mappedProps : this.props;
        const configErrors = mappedProps.configErrors;

        // in development mode, check if there is any server render error for module. If so, render error module so developer can address the issue.
        if (process.env.NODE_ENV === 'development' && !this.props.store.requestContext.features.disable_render_module_server_error) {
            const serverRenderError =
                this.props.store.requestCache?.internalCache && this.props.store.requestCache.internalCache[SERVER_RENDER_ERROR_CACHE_KEY];

            if (serverRenderError) {
                const moduleWithError = serverRenderError[this.getModuleServerRenderCacheKeyName(this.props.id, this.props.typeName)];
                if (moduleWithError && moduleWithError.item) {
                    return (
                        <ErrorModule
                            {...mappedProps}
                            errorType='error'
                            error={
                                new Error(
                                    `Error during server side rendering for module ${this.props.id} of type ${this.props.typeName}.
                                    NOTE: This error is only displayed in DEVELOPER mode. This won't affect PRODUCTION.
                                    This is a safety measure so developer can address issues that happen on the server.
                                    Please, address the following issue: ${moduleWithError.item.errorMessage}`
                                )
                            }
                            message={moduleWithError.item.errorStack}
                            renderModuleAttributes={this.renderModuleAttributes}
                            renderView={renderView}
                        />
                    );
                }
            }
        }

        const Component = mappedProps.component as React.ComponentClass;
        const lazyRenderModuleEnabled = isLazyRenderModuleEnabled(this.props.store.requestContext);
        if (!Component && !lazyRenderModuleEnabled) {
            const message = internalTelemetry.stringFormat(EXCEPTION_UNREGISTEREDMODULEID, [id, typeName]);
            internalTelemetry.log(LogLevel.Error, message);
            return (
                <ErrorModule
                    errorType='unregistered'
                    {...{ ...this.props, ...mappedProps }}
                    error={new Error(message)}
                    renderModuleAttributes={this.renderModuleAttributes}
                    renderView={renderView}
                />
            );
        }

        const err = this.state.error || (configErrors && configErrors.length && new Error(configErrors.map(x => x.message).join(', ')));
        if (err) {
            const element = renderErrorOrPlaceHolderModules(
                err,
                internalTelemetry,
                mappedProps,
                this.state,
                this.props,
                this._isEditorial(),
                this.renderModuleAttributes
            );
            if (element) {
                return element;
            }
        }

        if (process.env.CURRENT_ENVIRONMENT === 'node') {
            if (config && config.hasOwnProperty('clientRender') && config.clientRender && !this._isEditorial()) {
                if (lazyRenderModuleEnabled) {
                    return <div className={'ms-lazyload-empty-container'} style={{ height: 10, width: '90%' }} />;
                }

                return <React.Fragment />;
            } else {
                return this._doServerSideRender(mappedProps, Component, internalTelemetry);
            }
        } else if (!this.state.mounted && this.props.store.deferred(this.props.id)) {
            // module needs two passes
            return null;
        } else if (config && config.hasOwnProperty('clientRender') && config.clientRender && !this._isEditorial()) {
            if (lazyRenderModuleEnabled) {
                if (this.state.isLazyLoaded) {
                    return (
                        <AsyncClientRenderComponent
                            typeName={this.props.typeName}
                            requestCache={this.props.store.requestCache}
                            componentRef={this.componentRef}
                            mappedProps={mappedProps}
                            renderModuleAttributes={this.renderModuleAttributes}
                            renderView={renderView}
                            lazyLoadHandler={this.lazyLoadHandler}
                            scriptInitCompleted={true}
                        ></AsyncClientRenderComponent>
                    );
                }
                return (
                    <LazyLoad>
                        <AsyncClientRenderComponent
                            typeName={this.props.typeName}
                            requestCache={this.props.store.requestCache}
                            componentRef={this.componentRef}
                            mappedProps={mappedProps}
                            renderModuleAttributes={this.renderModuleAttributes}
                            renderView={renderView}
                            lazyLoadHandler={this.lazyLoadHandler}
                            scriptInitCompleted={false}
                        ></AsyncClientRenderComponent>
                    </LazyLoad>
                );
            } else if (this.state.mounted) {
                return (
                    <Component
                        {...mappedProps}
                        ref={this.componentRef}
                        {...{ renderModuleAttributes: this.renderModuleAttributes, renderView: renderView }}
                    />
                );
            } else {
                return <React.Fragment />;
            }
        }

        return (
            <Component
                {...mappedProps}
                ref={this.componentRef}
                {...{ renderModuleAttributes: this.renderModuleAttributes, renderView: renderView }}
            />
        );
    }

    private lazyLoadHandler = () => {
        this.setState({ isLazyLoaded: true });
    };

    private renderModuleAttributes = (props: IGeneric<IAny>): IDictionary<string> => {
        this.isRenderAttrributeCalled = true;

        if (!props || !props.id) {
            return {};
        }

        // Construct telemetry attributes for module
        const telemetryId: string = props.friendlyName || props.id;
        const telemetryAttributes: IDictionary<string> =
            this.props.internalTelemetry && this.props.internalTelemetry.setTelemetryAttribute
                ? this.props.internalTelemetry.setTelemetryAttribute(telemetryId)
                : {};

        const moduleAttributes: IDictionary<string> = { 'data-m-t': props.typeName, ...telemetryAttributes };
        if (this.shouldDisplayErrors || this._isEditorial()) {
            moduleAttributes['data-i'] = this._setDataInfoAttribute(props);
        }

        if (props.platform && typeof props.platform === 'object') {
            Object.keys(props.platform).forEach(key => {
                moduleAttributes[`data-m-${key}`] = props.platform[key];
            });
        }

        // If a module is associated with an experiment, add this attribtue to track when to activate the experiment
        if (props.context.request && props.context.request.experiments?.moduleIdToExperimentIdMap) {
            if (props.context.request.experiments.moduleIdToExperimentIdMap[props.id]) {
                moduleAttributes[`data-exp`] = `id:${props.context.request.experiments.moduleIdToExperimentIdMap[props.id]}`;
            }
        }
        // In editor scenario, Authoring will update the module layout by sending us updates on
        // the MODULE_LAYOUT_FIELD in the config object

        if (this._isEditorial() && props.config && props.config[MODULE_LAYOUT_FIELD]) {
            moduleAttributes[`data-m-layout`] = props.config[MODULE_LAYOUT_FIELD];
        }

        return moduleAttributes;
    };

    /**
     * Set the data info attribute to be added to the modules
     * @param props The module props.
     */
    private _setDataInfoAttribute(props: IGeneric<IAny>): string {
        const dataInfo = [`id:${props.id}`];
        props.slotId && dataInfo.push(`s:${props.slotId}`);
        props.parentId && dataInfo.push(`p:${props.parentId}`);
        props.typeName && dataInfo.push(`t:${props.typeName}`);
        props.friendlyName && dataInfo.push(`fn:${props.friendlyName}`);
        const moduleBinder = (msdyn365Commerce as any).moduleBinder(props.typeName);
        if (moduleBinder) {
            moduleBinder.$type && dataInfo.push(`mt:${moduleBinder.$type}`);
        }
        props.errorType && dataInfo.push(`e:${props.errorType}`);
        return `{${dataInfo.join(',')}}`;
    }

    /**
     * isEditor
     * Determines if request is coming from the editor
     */
    private _isEditorial(): boolean {
        return _get(this.props, ['context', 'request', 'params', 'isEditor'], false);
    }

    /**
     * Creates data info string to be used by cms e.g '{id:prop-id,s:content,p:parent-id}'
     */
    private _generateDataInfo(): string {
        const dataInfo = [`id:${this.props.id}`];
        this.props.slotId && dataInfo.push(`s:${this.props.slotId}`);
        this.props.parentId && dataInfo.push(`p:${this.props.parentId}`);
        this.props.typeName && dataInfo.push(`t:${this.props.typeName}`);
        this.props.friendlyName && dataInfo.push(`fn:${this.props.friendlyName}`);
        const moduleBinder = (msdyn365Commerce as any).moduleBinder(this.props.typeName);
        if (moduleBinder) {
            moduleBinder.$type && dataInfo.push(`mt:${moduleBinder.$type}`);
        }

        // Error scenarios
        // 1. Empty module
        // 2. Unregistered module
        // 3. Generic error
        !this.state.isConfigured && dataInfo.push('e:empty');
        !this.state.isRegistered && dataInfo.push('e:unregistered');
        this.state.error && dataInfo.push('e:error');

        return `{${dataInfo.join(',')}}`;
    }

    /**
     * Conducts the server side render and returns the result.
     * If disable SSR Error Checking flag is turned on, the React Element is returned immediately,
     * otherwise the render result is calculated using renderToString to catch errors
     * and defer modules (used for editor and debug scenarios)
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _doServerSideRender(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mappedProps: any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Component: React.ComponentClass<{}, any>,
        internalTelemetry: InternalTelemetry
    ): JSX.Element {
        let renderHtmlString: string;
        const renderHtml = (
            <CoreContext.Provider value={this.props.context}>
                <Component {...mappedProps} {...{ renderModuleAttributes: this.renderModuleAttributes, renderView: renderView }} />
            </CoreContext.Provider>
        );
        if (this.disableSSRErrorChecks) {
            // In non editorial or non debug mode and if app setting to disable server side error checking is turned on
            // return the JSX directly without computing the render result to save time and compute cost
            return renderHtml;
        } else {
            try {
                // eslint-disable-next-line
                renderHtmlString = require('react-dom/server').renderToString(renderHtml);
                if (!renderHtmlString.length) {
                    // module returned null
                    this.props.store.markModuleAsDeferred(this.props.id);
                }
            } catch (e) {
                this.addFailingModuleServerRenderToCache(e);

                internalTelemetry.log(LogLevel.Error, `Safe Render module Failed to render type-'{typeName}' with id-'{id}'`, {
                    exception: e,
                    values: [asSystemMetadata(this.props.typeName), asSystemMetadata(this.props.id)]
                });
                return (
                    <ErrorModule
                        {...mappedProps}
                        errorType='error'
                        error={e}
                        message={e && e.stack}
                        renderModuleAttributes={this.renderModuleAttributes}
                        renderView={renderView}
                    />
                );
            }
            return React.createElement(SDK_FRAGMENT_NAME, { key: this.props.id, dangerouslySetInnerHTML: { __html: renderHtmlString } });
        }
    }

    private addFailingModuleServerRenderToCache(error: any) {
        const cacheKey = this.buildModuleServerRenderCacheKey(this.props.id, this.props.typeName);
        const cacheItem = {
            item: {
                moduleId: this.props.id,
                moduleType: this.props.typeName,
                errorName: error && error.name,
                errorMessage: error && error.message,
                errorStack: error && error.stack
            }
        };

        this.props.store.requestCache.put(cacheKey, cacheItem);
    }

    private buildModuleServerRenderCacheKey(moduleId: string, moduleType: string): ICacheKey {
        return {
            key: this.getModuleServerRenderCacheKeyName(moduleId, moduleType),
            typeName: SERVER_RENDER_ERROR_CACHE_KEY
        };
    }

    private getModuleServerRenderCacheKeyName(moduleId: string, moduleType: string): string {
        return `${moduleId}:${moduleType}`.toUpperCase();
    }

    /**
     * Assignes data-i and data-t attributes to child component if isEditor is enabled
     */
    private _setDataAttributes(): void {
        let refNode;
        if (this._isEditorial()) {
            // If type name only exists on the props then this module is not registered.
            if (this.state.error || !this.state.isRegistered || !this.state.isConfigured) {
                refNode = document.getElementById(this.props.id);
            } else if (this.componentRef.current) {
                /* eslint-disable react/no-find-dom-node */
                refNode = this.props.root
                    ? document.getElementById(LAYOUT_MOUNT_POINT)
                    : (ReactDOM.findDOMNode(this.componentRef.current) as Element);
            }
        }

        if (refNode && typeof refNode.setAttribute === 'function') {
            refNode.setAttribute('data-i', this._generateDataInfo());
        }
    }

    private _setErrorStates(): void {
        const isMounted = this.state.mounted;
        const mappedProps = mapStateToProps(this.props.store, this.props, isMounted);

        // Registration check
        const isRegistered = !!mappedProps.component;
        if (isRegistered !== this.state.isRegistered) {
            this.setState({ ...this.state, isRegistered: isRegistered });
            return;
        }

        // Configuration check
        const isConfigured = !(mappedProps.configErrors && mappedProps.configErrors.length > 0);
        if (isConfigured !== this.state.isConfigured) {
            this.setState({ ...this.state, isConfigured: isConfigured });
            return;
        }
    }
}

export const getRenderSlotComponent = (
    requestCache: any,
    parentProps: IGeneric<IAny>,
    moduleConfigs: IModuleContract[],
    slotId: string,
    addSiblingIndices?: number[],
    isMounted?: boolean
): React.ReactNode[] => {
    if (!moduleConfigs) {
        return [];
    }

    const canAddAtIndex = (index: number) => {
        return addSiblingIndices ? addSiblingIndices.indexOf(index + 1) >= 0 : false;
    };
    // if addSiblingIndices is not undefined, we add the AddModuleControl components between each module; but in hidden state if index value isnt provider
    // this prevents re-rendering of the module when the AddModuleControl components are enabled/disabled
    // in preview mode, addSiblingIndices will be undefined and we will only render the modules
    return moduleConfigs.map((moduleConfig: IModuleContract, index: number) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const config =
            (!!!parentProps?.store?.requestContext?.features?.disable_moduleConfig_optimize &&
                requestCache.getValue({ typeName: MODULE_CONFIG_KEY, key: moduleConfig.id })) ||
            moduleConfig.config;
        return addSiblingIndices && isMounted ? (
            <AddModuleWrapper
                id={moduleConfig.id}
                safeRenderProps={{
                    key: moduleConfig.id,
                    ...moduleConfig,
                    config: config,
                    parentId: parentProps.id,
                    slotId: slotId,
                    store: parentProps.store,
                    addChunk: parentProps.addChunk
                }}
                addModuleProps={{
                    moduleId: parentProps.id,
                    index: index + 1,
                    enabled: canAddAtIndex(index),
                    slotId: slotId,
                    mode: AddControlMode.Sibling
                }}
            />
        ) : (
            <SafeRenderModuleWithContext
                key={moduleConfig.id}
                {...moduleConfig}
                config={config}
                parentId={parentProps.id}
                slotId={slotId}
                store={parentProps.store}
                addChunk={parentProps.addChunk}
            />
        );
    });
};

/**
 * Maps the application state to the modules props
 * @param state The application state
 * @param ownProps The props for the specific module
 */
export const mapStateToProps = (store: PageContext, props: TSafeRenderModuleProps, isMounted?: boolean): IModule<IGeneric<IAny>> => {
    try {
        const { internalTelemetry } = props;
        const moduleConfig = getModuleConfig(store, props.id);
        const requestCache = store.requestCache;
        if (!moduleConfig) {
            internalTelemetry.log(
                LogLevel.Error,
                internalTelemetry.stringFormat(EXCEPTION_MISSINGMODULECONFIG, [props.id, props.typeName])
            );
            return {} as IModule<IGeneric<IAny>>;
        }

        // TODO - following statements introducing side effect in render method (props should not change in render). Please take a look after public preview
        // Pull value out of stuff that came from cache (doing this gives us reactivity via MobX)
        Object.keys(moduleConfig.data).forEach(key => {
            if (moduleConfig.data[key] && moduleConfig.data[key].Item) {
                moduleConfig.data[key] = moduleConfig.data[key].Item;
            }
        });

        const moduleBinder = (msdyn365Commerce as any).moduleBinder(moduleConfig.typeName);
        if (!moduleBinder && !isLazyRenderModuleEnabled(store.requestContext)) {
            internalTelemetry.log(LogLevel.Error, EXCEPTION_NOMODULEBINDER, { values: [asSystemMetadata(moduleConfig.typeName)] });
            return {} as IModule<IGeneric<IAny>>;
        }
        const enableAddModuleControls = isAuthoringEditEnabled(store.requestContext);

        return {
            ...props,
            ...moduleConfig,
            component: moduleBinder && moduleBinder.component,
            slots: enableAddModuleControls
                ? getSlotDictionary(requestCache, props, moduleConfig.slotsForAddModule, isMounted)
                : getSlotDictionary(requestCache, props)
        };
    } catch (e) {
        props.internalTelemetry.log(LogLevel.Error, props.internalTelemetry.stringFormat(EXCEPTION_MODULERENDERFAIL, [props.id]), {
            exception: e
        });
        return {} as IModule<IGeneric<IAny>>;
    }
};

const getModuleConfig = (store: PageContext, moduleId: string): IModule => {
    return store.modules(moduleId);
};

const getSlotDictionary = (
    requestCache: any,
    props: IGeneric<IAny>,
    slotsForAddModule?: IAddModuleSlot[],
    isMounted?: boolean
): IDictionary<React.ReactNode[]> => {
    const slotDictionary: IDictionary<React.ReactNode[]> = {};
    const { modules } = props;
    const slotsWithAddModuleControls = (slotsForAddModule || []).filter(a => !a.index && a.slotName);
    const addSiblingIndexDictionary: { [key: string]: number[] } = {};
    (slotsForAddModule || [])
        .filter(a => a.index && !a.slotName)
        .forEach(a => {
            addSiblingIndexDictionary[a.slotId] = a.index || [];
        });
    // removes placeholder 'click here to configure' modules
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getNonPlaceholderModules = (mods: any[]) => {
        return mods.filter(a => a.typeName !== 'place-holder-for-container-preview');
    };
    if (modules) {
        Object.keys(modules).forEach((key: string) => {
            slotDictionary[key] = getRenderSlotComponent(
                requestCache,
                props,
                getNonPlaceholderModules(modules[key]),
                key,
                slotsForAddModule ? addSiblingIndexDictionary[key] || [] : undefined,
                isMounted
            );
        });
    }
    slotsWithAddModuleControls.forEach((slot: IAddModuleSlot) => {
        if (isMounted && (!modules || !modules[slot.slotId] || getNonPlaceholderModules(modules[slot.slotId]).length === 0)) {
            slotDictionary[slot.slotId] = [
                <AddModuleControl
                    key={`${props.id}.${slot.slotId}`}
                    moduleId={props.id}
                    enabled={true}
                    slotId={slot.slotId}
                    parentId={props.parentId}
                    slotName={slot.slotName}
                    mode={AddControlMode.EmptySlot}
                />
            ];
        }
    });
    return slotDictionary;
};

const SafeRenderModuleWithContext = WithContext(SafeRenderModule);
export default SafeRenderModuleWithContext;
